import { useState } from 'react';
import type { ReactElement } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import type { GoogleConnection } from '../../api/types';
import { useDisconnectGoogle, useGoogleConnections } from '../../api/source-queries';
import { useSession } from '../../app/session-context';
import { ErrorBanner } from '../../components/ErrorBanner';
import { ConnectGoogleButton, GoogleErrorBanner } from '../../sources/connect';
import { Badge } from '../../ui/Badge';
import { Card } from '../../ui/Card';
import { ConfirmDialog } from '../../ui/Dialog';
import { Button } from '../../ui/Button';
import { Stack, Row } from '../../ui/Stack';
import { StatusState } from '../../ui/StatusState';

const settingsApi = getRouteApi('/w/$workspaceId/settings');

const DISCONNECT_MESSAGE =
  'Linked Sheets stay linked but cannot be pulled until you connect Google again.';

function Status({ connection }: { connection: GoogleConnection }): ReactElement {
  return connection.status === 'active' ? (
    <Badge tone="success">Connected</Badge>
  ) : (
    <Badge tone="warn">Needs reconnect</Badge>
  );
}

/** The signed-in person's Google connection, and the other members' read-only. */
export function SourcesSettings(): ReactElement {
  const { snapshot } = useSession();
  const workspaceId = snapshot.workspaceId ?? '';
  const enabled = Boolean(snapshot.config?.googleSheets);
  const { google_error: googleError } = settingsApi.useSearch();

  if (!enabled) {
    return (
      <Card title="Sources">
        <StatusState kind="empty" title="Google Sheets is not enabled on this server." />
      </Card>
    );
  }
  return <Connections workspaceId={workspaceId} googleError={googleError} />;
}

function Connections({ workspaceId, googleError }: { workspaceId: string; googleError: string | undefined }): ReactElement {
  const connections = useGoogleConnections();
  const disconnect = useDisconnectGoogle();
  const [confirming, setConfirming] = useState(false);
  const returnTo = `/w/${workspaceId}/settings/sources`;
  const list = connections.data?.connections ?? [];
  const mine = list.find((c) => c.mine);
  const others = list.filter((c) => !c.mine);

  return (
    <Card title="Google">
      <Stack gap={4}>
        <GoogleErrorBanner code={googleError} />
        {mine ? (
          <Row gap={2}>
            <span>{mine.accountEmail}</span>
            <Status connection={mine} />
            <ConnectGoogleButton returnTo={returnTo} label="Reconnect Google" />
            <Button variant="quiet" isDisabled={disconnect.isPending} onPress={() => setConfirming(true)}>
              Disconnect
            </Button>
          </Row>
        ) : connections.data ? (
          <div>
            <ConnectGoogleButton returnTo={returnTo} />
          </div>
        ) : null}

        {others.length > 0 ? (
          <Stack gap={2}>
            <p className="m-0">Other members</p>
            {others.map((c) => (
              <Row key={c.id} gap={2}>
                <span>{c.accountEmail}</span>
                <Status connection={c} />
              </Row>
            ))}
          </Stack>
        ) : null}

        <ErrorBanner error={connections.error ?? disconnect.error} />
      </Stack>
      <ConfirmDialog
        title="Disconnect Google?"
        message={DISCONNECT_MESSAGE}
        confirmLabel="Disconnect"
        tone="danger"
        isOpen={confirming}
        onOpenChange={setConfirming}
        onConfirm={() => disconnect.mutate()}
      />
    </Card>
  );
}
